import { cn } from '@rocco/utils/cn';

/**
 * The product-tile geometry, shared by every surface that lays product tiles out
 * on a detail page — the range row, the similar-products row and its standalone
 * mobile section, the supplier carousels, and the professional page's Products
 * grid.
 *
 * All of it mirrors the /products browse grid — the REVAMP one, whose portrait
 * grid is what these pages sit alongside — so a product tile is the same card
 * wherever it appears: its media ratio, its title and brand type, and the
 * grid's column gap and row rhythm. Read these rather than restating them.
 */

/** The detail-page product grid: 2-up on mobile, 5-up from md, with its gaps. */
export const PRODUCT_TILE_GRID =
    'grid grid-cols-2 gap-x-2 gap-y-12 md:grid-cols-5 md:gap-x-3 md:gap-y-[6.25rem]';

/**
 * The browse product card's ratio (`aspectRatio: 'productTall'` on the portrait
 * grid — see the `portrait` branch in `routes/_app.products.$`).
 *
 * The square legacy grid is NOT the reference: it is what `/products` serves
 * with the browse revamp off, so measuring a leaf category page on a flag-off
 * environment reads square and is misleading.
 *
 * Stated per breakpoint because it overrides `DetailTileCard`'s own `max-md:` /
 * `md:` aspect pair — an unprefixed class can't win against a variant-prefixed
 * one.
 */
export const PRODUCT_TILE_MEDIA = cn(
    'max-md:aspect-tile-product-tall md:aspect-tile-product-tall',
    // The corner comes from `rounded-tile` on the card itself — 32px, the same
    // as every other product tile on the site. This grid used to force 8px,
    // which made one tile carry two different corners depending on the page.
    //
    // Published for the hover strips, which sit inside this frame and are
    // clipped by it — they read the corner instead of hardcoding one.
    '[--tile-corner:2rem]',
);

/**
 * The browse product card's title and brand lines, mirroring rocco's
 * `ProductTile` under `isPortrait` — 16px for both, the title medium on
 * #323232 and the brand normal on #767676.
 *
 * `DetailTileCard`'s own type is the 22px editorial tile from the detail-page
 * Figma. That is the right size for a project or article tile carrying a
 * headline, but on a product it set the brand line as large as the product
 * name and both a step and a half above the same product on the browse grid.
 */
export const PRODUCT_TILE_TITLE =
    'md:text-[1rem] md:leading-[1.3] md:font-medium md:text-[#323232]';
export const PRODUCT_TILE_SUBTITLE =
    'md:text-[1rem] md:leading-[1.3] md:font-normal md:text-[#767676]';
